/**
 * AdminEmployersPage.js — Founder Operational Console, Pass 1.
 *
 * Strictly READ-ONLY operational visibility surface. Implements
 * audited SAFE-NOW targets:
 *   A — Founder dashboard (list view + drill-down)
 *   B — Employer list table
 *   D — Activation visibility (read-only badge derived from
 *        emp_plan_entitlements presence)
 *   E — Active-role counts
 *   F — Reviewer counts (distinct active reviewer_user_id)
 *   G — Pending-review counts (pending + in_review)
 *   H — Recent operational activity (last activity ts + recent
 *        audit rows on drill-down)
 *   J — Audit search/filter (action, since, limit)
 *   N — Operational health indicators (derived in-page)
 *
 * EXPLICITLY out of scope:
 *   - C (activate/deactivate writes)
 *   - I (matching intervention)
 *   - K (founder resend)
 *   - L (reviewer disable)
 *   - M (reopen review)
 *
 * Zero writes. Manual refresh only. No polling, no realtime.
 *
 * Surface gating:
 *   - WorkspaceGuard workspace="admin" (route-level, App.js)
 *   - OperatorOnlyRoute            (route-level, App.js)
 *   - Backend require_admin        (server-authoritative)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Building2, RefreshCw, Loader2, AlertTriangle, Search,
  CheckCircle2, Clock, Users, UserCheck, Briefcase, MailPlus,
  ClipboardList, Filter,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { planLabel } from '@/lib/employerPlanLabels';
import { isActiveRoleStatus } from '@/lib/roleLifecycle';


/* ── Constants ─────────────────────────────────────────────── */

// RC 1.62 Stabilisation Slice (Part E · R-12, 2026-02-28) — admin
// staleness thresholds were previously hardcoded here. They are now
// resolved at runtime via `GET /api/admin/v1/admin-limits` (which
// reads `app_settings.ADMIN.STALE_THRESHOLD_DAYS` /
// `ADMIN.NO_ACTIVE_ROLE_THRESHOLD_DAYS`). The literals below remain
// as a documented in-code fallback used ONLY when the endpoint is
// unreachable on first paint. The fallback preserves prior
// behaviour exactly.
const STALE_THRESHOLD_DAYS_FALLBACK = 14;
const NO_ACTIVE_ROLE_THRESHOLD_DAYS_FALLBACK = 30;
const AUDIT_ACTIONS = [
  'all',
  /* GC-1 — account */
  'account.created',
  /* GC-2 — invitation lifecycle */
  'member.invited',
  'member.invitation_accepted',
  'member.invitation_revoked',
  'member.invitation_expired',
  /* GC-3 — single member revocation */
  'member.revoked',
  /* GC-4 — role modification */
  'member.role_modified',
  /* GC-5 — suspension lifecycle */
  'member.suspended',
  'member.suspended_emergency',
  'member.unsuspended',
  'member.suspension_expired',
  /* GC-6 — bulk operations */
  'member.bulk_invited',
  'member.bulk_revoked',
  'member.bulk_role_modified',
];


/* ── Helpers ───────────────────────────────────────────────── */

function fmtDate(iso) {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function ageDays(iso) {
  if (!iso) return Infinity;
  const ms = Date.now() - new Date(iso).getTime();
  return ms / (1000 * 60 * 60 * 24);
}

function deriveHealth(emp, thresholds) {
  // Health chips are derived purely from the existing counters —
  // no schema invention, no hidden state.
  // RC 1.62 Stabilisation Slice (Part E · R-12) — thresholds now
  // injected from server-projected `app_settings`.
  const staleDays      = thresholds?.stale_threshold_days ?? STALE_THRESHOLD_DAYS_FALLBACK;
  const noActiveDays   = thresholds?.no_active_role_threshold_days ?? NO_ACTIVE_ROLE_THRESHOLD_DAYS_FALLBACK;
  const flags = [];
  if (emp.active_role_count != null && emp.active_role_count === 0
      && ageDays(emp.created_at) > noActiveDays) {
    flags.push({ key: 'no_active_roles_30d', label: `No active roles (${noActiveDays}d+)` });
  }
  if (emp.member_count != null && emp.member_count <= 1) {
    flags.push({ key: 'owner_only', label: 'Owner only' });
  }
  if ((emp.active_role_count || 0) > 0
      && emp.active_reviewer_count != null
      && emp.active_reviewer_count === 0) {
    flags.push({ key: 'no_reviewer', label: 'Roles without reviewers' });
  }
  if (emp.last_activity_at && ageDays(emp.last_activity_at) > staleDays) {
    flags.push({ key: 'stale', label: `Stale (${staleDays}d+)` });
  }
  return flags;
}


/* ── Page ──────────────────────────────────────────────────── */

export default function AdminEmployersPage() {
  const [employers, setEmployers]     = useState([]);
  const [loading,   setLoading]       = useState(true);
  const [error,     setError]         = useState(null);
  const [partialFailures, setPartialFailures] = useState(null);
  const [query,     setQuery]         = useState('');
  const [healthFilter, setHealthFilter] = useState('all');
  const [activationFilter, setActivationFilter] = useState('all');
  const [selected,  setSelected]      = useState(null); // account_id
  // RC 1.62 Stabilisation Slice (Part E · R-12) — admin staleness
  // thresholds resolved server-side at first paint. Null until
  // loaded; deriveHealth() falls back to the documented in-code
  // defaults when null. Hidden NEVER means stale data — the page
  // simply uses the fallback until the projection arrives.
  const [adminThresholds, setAdminThresholds] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPartialFailures(null);
    try {
      const resp = await apiFetch('/api/admin/employers');
      setEmployers(resp?.employers || []);
      setPartialFailures(resp?.partial_failures || null);
    } catch (e) {
      setError(e?.message || 'Failed to load employers.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Project admin thresholds once at mount. Failure is non-fatal —
  // the deriveHealth helper falls back to the documented in-code
  // defaults preserving exact prior behaviour.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch('/api/admin/v1/admin-limits');
        if (!cancelled) setAdminThresholds(r);
      } catch {
        if (!cancelled) setAdminThresholds(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let rows = employers;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      rows = rows.filter((e) =>
        (e.name || '').toLowerCase().includes(q)
        || (e.sector || '').toLowerCase().includes(q)
        || (e.account_id || '').toLowerCase().includes(q),
      );
    }
    if (activationFilter !== 'all') {
      const want = activationFilter === 'activated';
      rows = rows.filter((e) => Boolean(e.activated) === want);
    }
    if (healthFilter !== 'all') {
      rows = rows.filter((e) =>
        deriveHealth(e, adminThresholds).some((h) => h.key === healthFilter),
      );
    }
    return rows;
  }, [employers, query, activationFilter, healthFilter, adminThresholds]);

  // Aggregate health-chip counts across the unfiltered list.
  const healthCounts = useMemo(() => {
    const c = {};
    for (const emp of employers) {
      for (const h of deriveHealth(emp, adminThresholds)) {
        c[h.key] = (c[h.key] || 0) + 1;
      }
    }
    return c;
  }, [employers, adminThresholds]);

  // Resolved thresholds for chip labels — fall back to documented
  // in-code defaults until the projection arrives.
  const staleDays    = adminThresholds?.stale_threshold_days
                        ?? STALE_THRESHOLD_DAYS_FALLBACK;
  const noActiveDays = adminThresholds?.no_active_role_threshold_days
                        ?? NO_ACTIVE_ROLE_THRESHOLD_DAYS_FALLBACK;

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="admin-employers-page">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ndor-ink flex items-center gap-2">
            <Building2 className="h-5 w-5 text-ndor-ink-muted" />
            Founder console &mdash; Employers
          </h1>
          <p className="text-sm text-ndor-ink-muted mt-1">
            Operational visibility across employer tenants. Read-only.
            No write paths. No tenant impersonation.
          </p>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={load} disabled={loading}
          data-testid="admin-employers-refresh"
        >
          {loading
            ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            : <RefreshCw className="h-4 w-4 mr-1.5" />}
          Refresh
        </Button>
      </header>

      {partialFailures && partialFailures.length > 0 && (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 flex items-start gap-2"
          data-testid="admin-employers-partial-failures"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong>Partial read failure:</strong> {partialFailures.join(', ')}.
            Affected counters render as &mdash; for every row. Other
            counters are accurate.
          </div>
        </div>
      )}

      {/* ── Health overview chips ───────────────────────────── */}
      <Card data-testid="admin-employers-health-overview">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-ndor-ink-muted" />
            Operational health
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2 text-xs">
          <HealthChip label={`No active roles (${noActiveDays}d+)`} k="no_active_roles_30d"
                       count={healthCounts.no_active_roles_30d || 0}
                       active={healthFilter === 'no_active_roles_30d'}
                       onClick={() => setHealthFilter(healthFilter === 'no_active_roles_30d' ? 'all' : 'no_active_roles_30d')} />
          <HealthChip label="Owner only" k="owner_only"
                       count={healthCounts.owner_only || 0}
                       active={healthFilter === 'owner_only'}
                       onClick={() => setHealthFilter(healthFilter === 'owner_only' ? 'all' : 'owner_only')} />
          <HealthChip label="Roles without reviewers" k="no_reviewer"
                       count={healthCounts.no_reviewer || 0}
                       active={healthFilter === 'no_reviewer'}
                       onClick={() => setHealthFilter(healthFilter === 'no_reviewer' ? 'all' : 'no_reviewer')} />
          <HealthChip label={`Stale (${staleDays}d+)`} k="stale"
                       count={healthCounts.stale || 0}
                       active={healthFilter === 'stale'}
                       onClick={() => setHealthFilter(healthFilter === 'stale' ? 'all' : 'stale')} />
          {healthFilter !== 'all' && (
            <Button
              size="sm" variant="ghost"
              className="ml-auto"
              onClick={() => setHealthFilter('all')}
              data-testid="admin-employers-health-clear"
            >
              Clear filter
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ── Filters ──────────────────────────────────────────── */}
      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 py-4">
          <div className="grow min-w-[200px]">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-ndor-ink-muted" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="org name, sector, account_id"
                className="h-9 pl-8"
                data-testid="admin-employers-search"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Activation</Label>
            <Select value={activationFilter} onValueChange={setActivationFilter}>
              <SelectTrigger
                className="h-9 w-[160px]"
                data-testid="admin-employers-activation-filter"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="activated">Activated only</SelectItem>
                <SelectItem value="not_activated">Not activated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ── Employer list table ──────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium">
            Employers ({filtered.length}{filtered.length !== employers.length ? ` of ${employers.length}` : ''})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && employers.length === 0 ? (
            <div
              className="flex items-center gap-2 py-10 justify-center text-sm text-ndor-ink-muted"
              data-testid="admin-employers-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> Loading employers&hellip;
            </div>
          ) : error ? (
            <div
              className="py-10 text-center space-y-3"
              data-testid="admin-employers-error"
            >
              <AlertTriangle className="mx-auto h-8 w-8 text-rose-500" />
              <p className="text-sm font-medium text-rose-200">{error}</p>
              <Button variant="outline" size="sm" onClick={load}>
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="py-10 text-center text-sm text-ndor-ink-muted"
              data-testid="admin-employers-empty"
            >
              No employers match the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-employers-table">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-ndor-ink-muted">
                    <th className="py-2 pr-3">Organisation</th>
                    <th className="py-2 pr-3 hidden md:table-cell">Created</th>
                    <th className="py-2 pr-3 text-right">Members</th>
                    <th className="py-2 pr-3 text-right">Active roles</th>
                    <th className="py-2 pr-3 text-right hidden sm:table-cell">Reviewers</th>
                    <th className="py-2 pr-3 text-right hidden sm:table-cell">Open reviews</th>
                    <th className="py-2 pr-3 text-right hidden md:table-cell">Pending invites</th>
                    <th className="py-2 pr-3 hidden lg:table-cell">Activation</th>
                    <th className="py-2 pr-3 hidden lg:table-cell">Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((emp) => {
                    const health = deriveHealth(emp);
                    return (
                      <tr
                        key={emp.account_id}
                        className="border-b last:border-0 cursor-pointer hover:bg-ndor-surface-alt/40"
                        onClick={() => setSelected(emp.account_id)}
                        data-testid={`admin-employers-row-${emp.account_id}`}
                      >
                        <td className="py-2 pr-3">
                          <div className="font-medium text-ndor-ink">
                            {emp.name || <em className="text-ndor-ink-muted">(no name)</em>}
                          </div>
                          <div className="text-[10px] text-ndor-ink-muted font-mono">
                            {emp.account_id.slice(0, 8)}&hellip;
                          </div>
                          {health.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {health.map((h) => (
                                <Badge
                                  key={h.key}
                                  variant="outline"
                                  className="text-[10px] border-amber-300 text-amber-200 bg-amber-500/10"
                                  data-testid={`admin-employers-health-${emp.account_id}-${h.key}`}
                                >
                                  {h.label}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3 hidden md:table-cell text-xs text-ndor-ink-muted">
                          {fmtDate(emp.created_at)}
                        </td>
                        <NumCell value={emp.member_count} />
                        <NumCell value={emp.active_role_count} />
                        <NumCell value={emp.active_reviewer_count} hideOn="sm" />
                        <NumCell value={emp.open_review_count} hideOn="sm" />
                        <NumCell value={emp.pending_invite_count} hideOn="md" />
                        <td className="py-2 pr-3 hidden lg:table-cell">
                          <Badge
                            variant={emp.activated ? 'default' : 'outline'}
                            data-testid={`admin-employers-activation-${emp.account_id}`}
                          >
                            {emp.activated === null ? '\u2014' : (emp.activated ? 'Activated' : 'Placeholder')}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 hidden lg:table-cell text-xs text-ndor-ink-muted">
                          {fmtDate(emp.last_activity_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <EmployerDetailDialog
        accountId={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}


/* ── Sub-components ────────────────────────────────────────── */

function HealthChip({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors ${
        active
          ? 'border-ndor-ink bg-ndor-ink text-ndor-surface'
          : 'border-ndor-edge bg-ndor-surface text-ndor-ink hover:bg-ndor-surface-alt/60'
      }`}
      data-testid={`admin-employers-health-chip-${label.replace(/\W+/g, '-').toLowerCase()}`}
    >
      <span className="font-mono tabular-nums">{count}</span>
      <span>{label}</span>
    </button>
  );
}

function NumCell({ value, hideOn }) {
  const hide = hideOn === 'sm' ? 'hidden sm:table-cell'
             : hideOn === 'md' ? 'hidden md:table-cell'
             : '';
  return (
    <td className={`py-2 pr-3 text-right font-mono tabular-nums ${hide}`}>
      {value === null || value === undefined ? '\u2014' : value}
    </td>
  );
}


/* ── Drill-down dialog ─────────────────────────────────────── */

function EmployerDetailDialog({ accountId, onClose }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [actionFilter, setActionFilter] = useState('all');
  const [sinceFilter,  setSinceFilter]  = useState('');
  const [auditLimit,   setAuditLimit]   = useState(50);
  const [auditRows, setAuditRows]       = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError]     = useState(null);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true); setError(null);
    try {
      const resp = await apiFetch(`/api/admin/employers/${accountId}`);
      setData(resp);
    } catch (e) {
      setError(e?.message || 'Failed to load detail.');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  const loadAudit = useCallback(async () => {
    if (!accountId) return;
    setAuditLoading(true); setAuditError(null);
    try {
      const q = new URLSearchParams();
      q.set('limit', String(auditLimit));
      if (actionFilter && actionFilter !== 'all') q.set('action', actionFilter);
      if (sinceFilter) q.set('since', sinceFilter);
      const resp = await apiFetch(
        `/api/admin/employers/${accountId}/audit?${q.toString()}`,
      );
      setAuditRows(resp?.rows || []);
    } catch (e) {
      setAuditError(e?.message || 'Failed to load audit.');
    } finally {
      setAuditLoading(false);
    }
  }, [accountId, actionFilter, sinceFilter, auditLimit]);

  useEffect(() => {
    if (accountId) {
      setData(null); setAuditRows(null);
      setActionFilter('all'); setSinceFilter(''); setAuditLimit(50);
      load();
    }
  }, [accountId, load]);

  if (!accountId) return null;
  const org = data?.organisation || {};
  const members = data?.members || [];
  const invitations = data?.invitations || [];
  const reviewers = data?.reviewer_assignments || [];
  const roles = data?.roles || [];
  const recentAudit = data?.recent_audit || [];

  return (
    <Dialog open={Boolean(accountId)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="admin-employer-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {org.name || <span className="font-mono text-xs">{accountId.slice(0, 8)}&hellip;</span>}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 py-6 justify-center text-sm text-ndor-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading detail&hellip;
          </div>
        )}
        {error && (
          <div className="py-4 text-sm text-rose-200" data-testid="admin-employer-detail-error">
            {error}
          </div>
        )}

        {data && !loading && (
          <div className="space-y-5 text-sm">
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat icon={<Users className="h-3.5 w-3.5" />}     label="Members"      value={members.length} />
              <Stat icon={<Briefcase className="h-3.5 w-3.5" />} label="Active roles" value={roles.filter((r) => isActiveRoleStatus(r?.status)).length} />
              <Stat icon={<UserCheck className="h-3.5 w-3.5" />} label="Reviewers"    value={new Set(reviewers.filter((r) => r.assignment_status === 'active').map((r) => r.reviewer_user_id)).size} />
              <Stat icon={<ClipboardList className="h-3.5 w-3.5" />} label="Open reviews" value={data.open_reviews_total ?? '\u2014'} />
            </div>

            {/* Activation badge */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={data.activated ? 'default' : 'outline'}>
                {data.activated ? <><CheckCircle2 className="h-3 w-3 mr-1" /> Activated</> : <><Clock className="h-3 w-3 mr-1" /> Placeholder</>}
              </Badge>
              {data.entitlement?.plan_tier && (
                <Badge variant="outline">
                  Plan: {planLabel(data.entitlement.plan_tier)}
                </Badge>
              )}
              <span className="text-xs text-ndor-ink-muted">
                Created {fmtDate(org.created_at)}
              </span>
            </div>

            {/* RC 1.62 Phase D — Channel-2 admin control */}
            <Channel2AdminControl
              accountId={accountId}
              entitlement={data.entitlement}
              onUpdated={(envelope) => {
                if (!envelope) return;
                setData((prev) => (prev ? {
                  ...prev,
                  entitlement: {
                    ...(prev.entitlement || {}),
                    channel_2_enabled: envelope.channel_2.enabled,
                  },
                } : prev));
              }}
            />

            {/* Members */}
            <Section title="Members" icon={<Users className="h-3.5 w-3.5" />}>
              {members.length === 0
                ? <Empty text="No members." />
                : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase text-ndor-ink-muted border-b">
                        <th className="py-1.5 pr-2">User</th>
                        <th className="py-1.5 pr-2">Role</th>
                        <th className="py-1.5 pr-2">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr key={m.user_id} className="border-b last:border-0">
                          <td className="py-1.5 pr-2 font-mono">{m.user_id.slice(0, 8)}&hellip;</td>
                          <td className="py-1.5 pr-2"><Badge variant="outline">{m.role}</Badge></td>
                          <td className="py-1.5 pr-2 text-ndor-ink-muted">{fmtDate(m.joined_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </Section>

            {/* Invitations */}
            <Section title={`Invitations (${invitations.length})`} icon={<MailPlus className="h-3.5 w-3.5" />}>
              {invitations.length === 0
                ? <Empty text="No invitations on this account." />
                : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase text-ndor-ink-muted border-b">
                        <th className="py-1.5 pr-2">Email</th>
                        <th className="py-1.5 pr-2">Role</th>
                        <th className="py-1.5 pr-2">Status</th>
                        <th className="py-1.5 pr-2">Issued</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invitations.slice(0, 20).map((inv) => (
                        <tr key={inv.id} className="border-b last:border-0">
                          <td className="py-1.5 pr-2 break-all">{inv.target_email}</td>
                          <td className="py-1.5 pr-2"><Badge variant="outline">{inv.target_role}</Badge></td>
                          <td className="py-1.5 pr-2"><Badge variant={inv.status === 'pending' ? 'default' : 'outline'}>{inv.status}</Badge></td>
                          <td className="py-1.5 pr-2 text-ndor-ink-muted">{fmtDate(inv.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </Section>

            {/* Reviewer coverage */}
            <Section title={`Reviewer coverage (${reviewers.length})`} icon={<UserCheck className="h-3.5 w-3.5" />}>
              {reviewers.length === 0
                ? <Empty text="No reviewer assignments." />
                : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[10px] uppercase text-ndor-ink-muted border-b">
                        <th className="py-1.5 pr-2">Reviewer</th>
                        <th className="py-1.5 pr-2">Role</th>
                        <th className="py-1.5 pr-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewers.slice(0, 50).map((r) => (
                        <tr key={r.assignment_id} className="border-b last:border-0">
                          <td className="py-1.5 pr-2 font-mono">{r.reviewer_user_id.slice(0, 8)}&hellip;</td>
                          <td className="py-1.5 pr-2 font-mono">{r.role_id.slice(0, 8)}&hellip;</td>
                          <td className="py-1.5 pr-2">
                            <Badge variant={r.assignment_status === 'active' ? 'default' : 'outline'}>
                              {r.assignment_status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              }
            </Section>

            {/* Recent audit summary (from detail bundle) */}
            <Section title="Recent activity (last 20)" icon={<Clock className="h-3.5 w-3.5" />}>
              {recentAudit.length === 0
                ? <Empty text="No audit rows yet." />
                : (
                  <ul className="space-y-1">
                    {recentAudit.map((row) => (
                      <li
                        key={row.id}
                        className="flex items-center justify-between rounded border px-2 py-1.5 text-xs"
                      >
                        <span className="font-mono">{row.action_class}</span>
                        <span className="text-ndor-ink-muted">{fmtDate(row.timestamp_utc)}</span>
                      </li>
                    ))}
                  </ul>
                )
              }
            </Section>

            {/* Audit filter panel */}
            <Section title="Audit search" icon={<Filter className="h-3.5 w-3.5" />}>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wide">Action</Label>
                  <Select value={actionFilter} onValueChange={setActionFilter}>
                    <SelectTrigger
                      className="h-8 w-[220px]"
                      data-testid="admin-employer-audit-action"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AUDIT_ACTIONS.map((a) => (
                        <SelectItem key={a} value={a}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide">Since (ISO 8601)</Label>
                  <Input
                    value={sinceFilter}
                    onChange={(e) => setSinceFilter(e.target.value)}
                    placeholder="2026-01-01T00:00:00Z"
                    className="h-8 w-[220px] font-mono text-xs"
                    data-testid="admin-employer-audit-since"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wide">Limit</Label>
                  <Input
                    type="number"
                    value={auditLimit}
                    min={1}
                    max={200}
                    onChange={(e) => setAuditLimit(Math.max(1, Math.min(200, Number(e.target.value) || 50)))}
                    className="h-8 w-[100px]"
                    data-testid="admin-employer-audit-limit"
                  />
                </div>
                <Button
                  size="sm" onClick={loadAudit} disabled={auditLoading}
                  data-testid="admin-employer-audit-run"
                >
                  {auditLoading
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Search className="h-3.5 w-3.5 mr-1.5" />}
                  Run query
                </Button>
              </div>

              {auditError && (
                <p className="text-xs text-rose-200 mt-2">{auditError}</p>
              )}
              {auditRows !== null && (
                <div className="mt-3" data-testid="admin-employer-audit-results">
                  {auditRows.length === 0 ? (
                    <Empty text="No rows match the filter." />
                  ) : (
                    <ul className="space-y-1">
                      {auditRows.map((row) => (
                        <li
                          key={row.id}
                          className="rounded border px-2 py-1.5 text-xs"
                          data-testid={`admin-employer-audit-row-${row.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono">{row.action_class}</span>
                            <span className="text-ndor-ink-muted">{fmtDate(row.timestamp_utc)}</span>
                          </div>
                          {row.actor_identity && (
                            <div className="text-[10px] text-ndor-ink-muted font-mono mt-0.5">
                              actor {String(row.actor_identity).slice(0, 8)}&hellip;
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </Section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ndor-ink-muted">
        {icon}<span>{label}</span>
      </div>
      <div className="mt-1.5 text-xl font-mono tabular-nums text-ndor-ink">
        {value === null || value === undefined ? '\u2014' : value}
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section>
      <h3 className="text-xs font-medium uppercase tracking-wide text-ndor-ink-muted mb-2 flex items-center gap-1.5">
        {icon}{title}
      </h3>
      {children}
    </section>
  );
}

function Empty({ text }) {
  return (
    <p className="text-xs text-ndor-ink-muted italic">{text}</p>
  );
}


// ─────────────────────────────────────────────────────────────
// RC 1.62 Phase D — admin-only Channel-2 toggle component
//   · RC 1.62 Phase D completion (Part D, 2026-02-28) — UX clarity
//     patch. Stronger CTA contrast (institutional blue), clearer
//     dormant-state copy ("Dormant · Admin Controlled"), concise
//     helper explanation, and per-state copy refinement. No
//     behavioural change — same endpoint, same audit trail, same
//     entitlement doctrine.
// ─────────────────────────────────────────────────────────────

function Channel2AdminControl({ accountId, entitlement, onUpdated }) {
  const enabled = Boolean(entitlement?.channel_2_enabled);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy]             = useState(false);

  const target = !enabled;

  const apply = async () => {
    setBusy(true);
    try {
      const res = await apiFetch(
        `/api/admin/employers/${accountId}/channel-2`,
        {
          method:  'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ channel_2_enabled: target }),
        },
      );
      toast.success(target ? 'Channel-2 enabled.' : 'Channel-2 disabled.');
      if (typeof onUpdated === 'function') onUpdated(res);
      setConfirming(false);
    } catch (err) {
      const code = err?.data?.detail?.code || err?.code || 'TOGGLE_FAILED';
      const msg  = err?.data?.detail?.message || err?.data?.message || err?.message || 'Toggle failed.';
      toast.error(`${code}: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  // Status pill copy — operator-facing language. Active state uses
  // "Active" rather than "Enabled" to mirror the entitlement-life-
  // cycle vocabulary used elsewhere in the admin surface; Dormant
  // state names the governance posture so secondary admins
  // understand the workspace is intentionally inactive (not broken).
  const statusLabel    = enabled ? 'Active' : 'Dormant · Admin Controlled';
  const statusTestid   = enabled ? 'enabled' : 'dormant';
  // Pill tone — emerald for live, neutral-blue for dormant. Both
  // remain readable against the dark NDOR surface; neither carries
  // a destructive (red) connotation.
  const pillClass = enabled
    ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
    : 'bg-blue-500/10 text-blue-200 border border-blue-500/30';

  return (
    <section
      data-testid="admin-channel-2-control"
      className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30 px-4 py-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ndor-ink-muted">
            Channel-2
          </span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${pillClass}`}
            data-testid={`admin-channel-2-status-${statusTestid}`}
          >
            {statusLabel}
          </span>
        </div>
        {!confirming && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirming(true)}
            className={
              enabled
                ? 'text-xs font-medium px-3 py-1.5 rounded border border-ndor-edge bg-ndor-bg hover:bg-ndor-surface text-ndor-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ndor-bg transition'
                : 'text-xs font-semibold px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ndor-bg transition disabled:opacity-50'
            }
            data-testid="admin-channel-2-toggle"
            aria-label={enabled ? 'Disable Channel-2 for this workspace' : 'Enable Channel-2 for this workspace'}
          >
            {enabled ? 'Disable Channel-2' : 'Enable Channel-2'}
          </button>
        )}
      </div>
      <p className="text-[12px] text-ndor-ink-muted leading-relaxed max-w-prose">
        Enabling Channel-2 allows this workspace to access governed
        Surfaced Candidates and Signals workflows, subject to entitlement
        and usage controls. Default is dormant; only admins can change
        this. Every change is audit-logged.
      </p>
      {confirming && (
        <div
          className="flex flex-wrap items-center gap-2 pt-1 border-t border-ndor-edge/60 mt-2"
          data-testid="admin-channel-2-confirm"
        >
          <span className="text-xs text-ndor-ink py-2">
            {target
              ? 'Confirm: enable Channel-2 for this workspace?'
              : 'Confirm: disable Channel-2 for this workspace?'}
          </span>
          <button
            type="button"
            disabled={busy}
            onClick={apply}
            className={
              target
                ? 'text-xs font-semibold px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ndor-bg'
                : 'text-xs font-semibold px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ndor-bg'
            }
            data-testid="admin-channel-2-confirm-yes"
          >
            {busy ? 'Applying…' : (target ? 'Yes, enable' : 'Yes, disable')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirming(false)}
            className="text-xs px-3 py-1.5 rounded border border-ndor-edge bg-ndor-bg hover:bg-ndor-surface text-ndor-ink disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ndor-bg"
            data-testid="admin-channel-2-confirm-no"
          >
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}
